/**
 * The active repository's files, the same view in the desktop app and in the browser.
 *
 * On the desktop they are read from the repository itself (git's own list: tracked files and new
 * ones not ignored), live, and published to DevPulse — paths only, never contents — so the web can
 * show the same tree. In a browser the tree is that last published list, read-only.
 */

import { useQuery } from '@tanstack/react-query'
import { api } from './api'
import { isDesktop, repoFiles } from './desktop'
import { folderFor } from './repoFolders'
import type { RepositoryLink } from './types'

export interface RepoView {
  files: string[]
  branch: string | null
  head: string | null
  /** Read from the folder on this machine, rather than the list the desktop last published. */
  live: boolean
  folder: string | null
  at: string | null
}

export function useRepoFiles(repository: RepositoryLink | null) {
  return useQuery({
    queryKey: ['repo-files', repository?.id],
    enabled: Boolean(repository),
    queryFn: async (): Promise<RepoView> => {
      const folder = isDesktop() && repository ? folderFor(repository.name) : null
      if (folder) {
        const seen = await repoFiles(folder)
        // What the web shows of this repository: published every time the desktop looks.
        void api.repositories.saveSnapshot(repository!.id, seen).catch(() => {})
        return { ...seen, live: true, folder, at: new Date().toISOString() }
      }
      const snapshot = await api.repositories.snapshot(repository!.id)
      return { files: snapshot?.files ?? [], branch: snapshot?.branch ?? null, head: snapshot?.head ?? null, live: false, folder: null, at: snapshot?.at ?? null }
    },
    staleTime: 30_000,
  })
}

export interface TreeNode {
  name: string
  path: string
  children?: TreeNode[]
}

/** Paths into a tree: folders first, then files, each by name. */
export function buildTree(paths: string[]): TreeNode[] {
  const root: TreeNode = { name: '', path: '', children: [] }
  for (const path of paths) {
    const parts = path.split('/').filter(Boolean)
    let node = root
    parts.forEach((part, i) => {
      const leaf = i === parts.length - 1
      const at = parts.slice(0, i + 1).join('/')
      let child = node.children!.find((c) => c.name === part && Boolean(c.children) === !leaf)
      if (!child) {
        child = leaf ? { name: part, path: at } : { name: part, path: at, children: [] }
        node.children!.push(child)
      }
      node = child
    })
  }
  const sort = (nodes: TreeNode[]): TreeNode[] =>
    nodes
      .sort((a, b) => Number(Boolean(b.children)) - Number(Boolean(a.children)) || a.name.localeCompare(b.name))
      .map((n) => (n.children ? { ...n, children: sort(n.children) } : n))
  return sort(root.children!)
}
